import { useCallback, useEffect, type FC } from "react";

import { useCurrentProjectFeatureEnabled } from "@ds/hooks/use-feature";
import { useVisibility } from "@ds/hooks/use-visibility";
import { useDeepRootSelector, usePagination, useRootDispatch, useRootSelector, useTableDataSorting } from "@ds/hooks";

import { isAggregatedEntity } from "@ds/model/entity-model";

import { AssetThumbnail } from "@ds/modules/assets/components/common/asset-thumbnail";
import { selectAssetById } from "@ds/modules/assets/redux/selectors/common-selectors";
import { UploadFilesModal } from "@ds/modules/assets/components/forms/upload-files-modal";
import { CreatePlaylistAssetModal } from "@ds/modules/content/playlist-assets/components/forms/create-playlist-asset-modal";
import { UpdatePlaylistAssetModal } from "@ds/modules/content/playlist-assets/components/forms/update-playlist-asset-modal";
import {
  selectAggregatedPlaylistAssetByIds,
  selectPlaylistAssetsIsLoading,
  selectSelectedPlaylistAssets,
  selectTablePlaylistAssets,
} from "@ds/modules/content/playlist-assets/redux/selectors/common-selectors";
import { playlistAssetsActions, PlaylistAssetsRemoteOperation } from "@ds/modules/content/playlist-assets/redux/slice";
import { FEATURE_KEYS } from "@ds/modules/features/@types";
import { DEFAULT_TABLE_TAB } from "@ds/modules/table-data/redux/slice";
import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { DetailsCard } from "@ds/components/details-view";
import { DndDropzone } from "@ds/components/dnd-dropzone";
import { MultipleValuesLabel } from "@ds/components/forms/labels/common";
import { SmallIcon } from "@ds/components/icons";
import { Loading } from "@ds/components/loaders/loading";
import { DataTableRowReorderEvent } from "@ds/components/table/data-table";
import { DetailsTable } from "@ds/components/table/details-table";
import { Column, getLastUpdatedColumn, getReorderColumn, getSelectionColumn } from "@ds/components/table/table-columns";
import { TotalSelectedLabel } from "@ds/components/table/total-selected-label";
import { Tooltip } from "@ds/components/tooltips";
import { LAST_UPDATED_COLUMN_FIELD } from "@ds/constants/table";
import { formatAbsoluteDate } from "@ds/utils/date";

import { getSupportedMediaTypes } from "../../utils/helpers";
import { PlaylistDetailsAssetsCardActionsMenu } from "./playlist-details-assets-card-actions-menu";

const isExpired = (endAt: string) => {
  if (!endAt) {
    return false;
  }
  return endAt && Date.parse(endAt) < Date.now();
};

const useGetThumbnailBodyTemplate = (playlistAsset: PlaylistAsset) => {
  const asset = useRootSelector(state => selectAssetById(state, playlistAsset?.asset_id || 0));
  return asset ? <AssetThumbnail asset={asset} /> : null;
};

const useGetAssetNameBodyTemplate = (playlistAsset: PlaylistAsset) => {
  const asset = useRootSelector(state => selectAssetById(state, playlistAsset?.asset_id || 0));
  return (asset && asset.name) ?? "";
};

const useGetAssetStartDateBodyTemplate = (playlistAsset: PlaylistAsset) => {
  return playlistAsset.start_at ? formatAbsoluteDate(playlistAsset.start_at) : null;
};

const useGetAssetEndDateBodyTemplate = (playlistAsset: PlaylistAsset) => {
  const tooltipId = `tooltip-${crypto.randomUUID()}`;
  const formattedDate = formatAbsoluteDate(playlistAsset.end_at);
  return isExpired(playlistAsset.end_at || "") ? (
    <>
      <Tooltip
        target={`#${tooltipId}`}
        content="The asset is expired and will no longer be shown in playback."
        position="top"
      />
      <SmallIcon id={tooltipId} className="pi pi-exclamation-circle" $warning /> {formattedDate}
    </>
  ) : (
    formattedDate
  );
};

export const PlaylistDetailsAssetsCard: FC<{ playlist: Playlist }> = ({ playlist }) => {
  const isAggregated = isAggregatedEntity(playlist);
  const [isCreateModalVisible, setIsCreateModalVisible] = useVisibility();
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useVisibility();

  const selectedEntities = useRootSelector(selectSelectedPlaylistAssets);
  const entities = useRootSelector(selectTablePlaylistAssets);

  const playlistAsset = useDeepRootSelector(state =>
    selectAggregatedPlaylistAssetByIds(
      state,
      selectedEntities.map(({ id }) => id),
    ),
  );

  const isLoading = useRootSelector(selectPlaylistAssetsIsLoading);
  const checkboxIsLoading = useRootSelector(state =>
    selectPlaylistAssetsIsLoading(state, PlaylistAssetsRemoteOperation.CHECKBOXES),
  );

  const [pagination] = usePagination(
    DetailsTableDataTypeEnum.ContentPlaylistAssets,
    isAggregated ? DEFAULT_TABLE_TAB : playlist.id.toString(),
    {
      playlistId: playlist.id,
    },
  );

  const [{ sortOrder, sortField }] = useTableDataSorting(
    DetailsTableDataTypeEnum.ContentPlaylistAssets,
    isAggregated ? DEFAULT_TABLE_TAB : playlist.id.toString(),
    {
      playlistId: playlist.id,
    },
  );

  const isPlaylistItemDurationEnabled = useCurrentProjectFeatureEnabled(FEATURE_KEYS.PLAYLIST_ITEM_DURATION);
  const isPlaylistItemStartEndEnabled = useCurrentProjectFeatureEnabled(FEATURE_KEYS.PLAYLIST_ITEM_START_END);

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (!isAggregated) {
      dispatch(
        playlistAssetsActions.fetchPlaylistAssets(
          { playlistId: playlist.id },
          { tableType: DetailsTableDataTypeEnum.ContentPlaylistAssets },
        ),
      );
    }
  }, [dispatch, isAggregated, playlist.id]);

  const onReorderHandler = useCallback(
    (currIndex: number, newIndex: number) => {
      if (currIndex !== newIndex) {
        console.log("onReorderHandler", currIndex, newIndex);
        dispatch(playlistAssetsActions.reorderPlaylistAssets({ currIndex, newIndex }, { playlistId: playlist.id }));
      }
    },
    [dispatch, playlist.id],
  );

  const onTableRowReorderHandler = useCallback(
    (e: DataTableRowReorderEvent<PlaylistAsset[]>) => {
      onReorderHandler(e.dragIndex, e.dropIndex);
    },
    [onReorderHandler],
  );

  const onToggleRowSelected = useCallback(
    (entry: PlaylistAsset) => dispatch(playlistAssetsActions.toggleSelected(entry.id)),
    [dispatch],
  );

  const onSingleRowSelectHandler = useCallback(
    (entry: PlaylistAsset) => dispatch(playlistAssetsActions.selectPlaylistAssets([entry])),
    [dispatch],
  );

  const onToggleAllHandler = useCallback(
    (isSelectedAll: boolean) => {
      if (isSelectedAll) {
        dispatch(
          playlistAssetsActions.fetchPlaylistAssets(
            { playlistId: playlist.id },
            undefined,
            PlaylistAssetsRemoteOperation.CHECKBOXES,
          ),
        );
      } else {
        dispatch(playlistAssetsActions.selectPlaylistAssets([]));
      }
    },
    [dispatch, playlist.id],
  );

  const onUploadHandler = useCallback(
    (files: File[]) => {
      if (files.length) {
        dispatch(playlistAssetsActions.uploadPlaylistAssets(files, { playlistId: playlist.id }));
      }
    },
    [dispatch, playlist.id],
  );

  const setIsMinimized = useCallback(
    (value: boolean) => () => dispatch(playlistAssetsActions.setUploadModalMinimized(value)),
    [dispatch],
  );

  return (
    <DetailsCard
      title="Assets"
      icon="video"
      actions={
        !isAggregated && (
          <PlaylistDetailsAssetsCardActionsMenu
            playlist={playlist}
            onCreate={setIsCreateModalVisible(true)}
            onUpdate={setIsUpdateModalVisible(true)}
            onUpload={onUploadHandler}
            onReorder={onReorderHandler}
            enableEdit={isPlaylistItemStartEndEnabled || isPlaylistItemDurationEnabled}
          />
        )
      }
    >
      <Loading isLoading={isLoading}>
        {!isAggregated && (
          <DndDropzone acceptMediaTypes={getSupportedMediaTypes(playlist)} onUpload={onUploadHandler}>
            <DetailsTable
              value={entities}
              className="playlist-assets-table"
              selection={selectedEntities}
              onSingleRowSelect={onSingleRowSelectHandler}
              toggleRowSelected={onToggleRowSelected}
              loading={isLoading}
              sortField={sortField}
              sortOrder={sortOrder}
              onRowReorder={onTableRowReorderHandler}
              rowClassName={rowData => {
                return isExpired(rowData.end_at || "") ? "dimmed" : "";
              }}
            >
              {getReorderColumn({ rowReorder: selectedEntities.length < 2 })}
              {getSelectionColumn({
                total: pagination.total,
                selected: selectedEntities.length,
                isLoading: checkboxIsLoading,
                onToggleAll: onToggleAllHandler,
              })}

              <Column header="File Name" body={useGetAssetNameBodyTemplate} resizeable={false} />
              <Column
                header="Thumbnail"
                body={useGetThumbnailBodyTemplate}
                className="center w200"
                resizeable={false}
              />
              {isPlaylistItemStartEndEnabled && (
                <Column
                  header="Start Date"
                  body={useGetAssetStartDateBodyTemplate}
                  className="center w175"
                  resizeable={false}
                />
              )}
              {isPlaylistItemStartEndEnabled && (
                <Column
                  header="End Date"
                  body={useGetAssetEndDateBodyTemplate}
                  className="center w175"
                  resizeable={false}
                />
              )}
              {isPlaylistItemDurationEnabled && (
                <Column field="duration" header="Duration" className="center w120" resizeable={false} />
              )}

              {getLastUpdatedColumn(LAST_UPDATED_COLUMN_FIELD, false)}
            </DetailsTable>

            <TotalSelectedLabel
              selectedCount={checkboxIsLoading ? Number(pagination.total) : selectedEntities.length}
            />
          </DndDropzone>
        )}

        {isAggregated && <MultipleValuesLabel />}
      </Loading>

      <UploadFilesModal tableType={MainTableDataTypeEnum.ContentPlaylists} setIsMinimized={setIsMinimized} />

      {isCreateModalVisible && (
        <CreatePlaylistAssetModal playlist={playlist} onClose={setIsCreateModalVisible(false)} />
      )}

      {isUpdateModalVisible && playlistAsset && (
        <UpdatePlaylistAssetModal initialValue={playlistAsset} onClose={setIsUpdateModalVisible(false)} />
      )}
    </DetailsCard>
  );
};
